import React, { useEffect, useMemo, useState } from "react"
import { getAPIBaseURL, getUser } from "../services/helpers"
import { getData, patchData, postDataWithForm } from "../services/request"

const EmployerProfile = () => {
  const user = getUser()
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState("")
  const [msg, setMsg] = useState("")

  const [form, setForm] = useState({
    name: "",
    industry: "",
    size: "1-10",
    website: "",
    description: "",
    registrationNumber: "",
    taxId: "",
    linkedInUrl: "",
    contactPersonName: "",
    contactPersonRole: "",
    contactEmail: "",
    contactPhone: "",
    location: { country: "", state: "", city: "", addressLine: "" },
  })

  const companyStatusLabel = useMemo(() => {
    const s = company?.status || "PENDING_VERIFICATION"
    if (s === "ACTIVE") return "Verified"
    if (s === "SUSPENDED") return "Suspended"
    return "Pending verification"
  }, [company?.status])

  const loadCompany = async () => {
    setLoading(true)
    setError("")
    setMsg("")
    const url = `${getAPIBaseURL()}/employme/employer/company`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error === "" && res.result?.status === 200) {
      const data = res.result.data?.data || res.result.data
      setCompany(data)
      setForm({
        name: data?.name || "",
        industry: data?.industry || "",
        size: data?.size || "1-10",
        website: data?.website || "",
        description: data?.description || "",
        registrationNumber: data?.registrationNumber || "",
        taxId: data?.taxId || "",
        linkedInUrl: data?.linkedInUrl || "",
        contactPersonName: data?.contactPersonName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
        contactPersonRole: data?.contactPersonRole || "HR",
        contactEmail: data?.contactEmail || user?.email || "",
        contactPhone: data?.contactPhone || user?.phoneNumber || "",
        location: {
          country: data?.location?.country || "",
          state: data?.location?.state || "",
          city: data?.location?.city || "",
          addressLine: data?.location?.addressLine || "",
        },
      })
    } else {
      setError(res.result?.data?.data || res.result?.data?.message || "Could not load company profile")
    }
  }

  useEffect(() => {
    loadCompany()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }
  const handleLocationChange = (key, value) => {
    setForm((prev) => ({ ...prev, location: { ...(prev.location || {}), [key]: value } }))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError("")
    setMsg("")
    const url = `${getAPIBaseURL()}/employme/employer/company`
    const headers = { "Content-Type": "application/json" }
    const res = await patchData(url, headers, form)
    setSaving(false)
    if (res.error === "" && res.result?.status === 200) {
      setCompany(res.result.data?.data || res.result.data)
      setMsg("Profile saved. If you updated key details, verification may be required again.")
    } else {
      setError(res.result?.data?.data || res.result?.data?.message || "Failed to save profile")
    }
  }

  const handleUploadDoc = async (file, label) => {
    if (!file) return
    setUploading(true)
    setError("")
    setMsg("")
    const url = `${getAPIBaseURL()}/employme/employer/company/verification-docs`
    const formData = new FormData()
    formData.append("document", file)
    formData.append("label", label || "Verification document")
    const res = await postDataWithForm(url, {}, formData)
    setUploading(false)
    if (res.error === "" && (res.result?.status === 201 || res.result?.status === 200)) {
      setMsg(res.result?.data?.data?.message || "Document uploaded")
      await loadCompany()
    } else {
      setError(res.result?.data?.data || res.result?.data?.message || "Failed to upload document")
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-3 sm:px-0">
      <div className="rounded-lg bg-white shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
          <h2 className="text-lg font-semibold text-gray-900 sm:text-2xl">
            Company profile
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Keep your details up to date and submit verification documents so admins can verify your company.
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Verification status:{" "}
            <span className="font-semibold">{companyStatusLabel}</span>
          </p>
        </div>
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
              {error}
            </div>
          ) : null}

          {msg ? (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-700">
              {msg}
            </div>
          ) : null}

          {!loading && company ? (
            <form onSubmit={handleSave} className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-gray-500">Company name</label>
                  <input
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Industry</label>
                  <input
                    value={form.industry}
                    onChange={(e) => handleChange("industry", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Company size</label>
                  <select
                    value={form.size}
                    onChange={(e) => handleChange("size", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  >
                    {["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Website</label>
                  <input
                    value={form.website}
                    onChange={(e) => handleChange("website", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="https://..."
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-gray-500">Company description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    rows={4}
                    placeholder="What does your company do?"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-gray-500">Registration number</label>
                  <input
                    value={form.registrationNumber}
                    onChange={(e) => handleChange("registrationNumber", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Tax ID</label>
                  <input
                    value={form.taxId}
                    onChange={(e) => handleChange("taxId", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-gray-500">LinkedIn</label>
                  <input
                    value={form.linkedInUrl}
                    onChange={(e) => handleChange("linkedInUrl", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="https://linkedin.com/company/..."
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-gray-500">Country</label>
                  <input
                    value={form.location.country}
                    onChange={(e) => handleLocationChange("country", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">State</label>
                  <input
                    value={form.location.state}
                    onChange={(e) => handleLocationChange("state", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">City</label>
                  <input
                    value={form.location.city}
                    onChange={(e) => handleLocationChange("city", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Address</label>
                  <input
                    value={form.location.addressLine}
                    onChange={(e) => handleLocationChange("addressLine", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-gray-500">Contact person name</label>
                  <input
                    value={form.contactPersonName}
                    onChange={(e) => handleChange("contactPersonName", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Contact person role</label>
                  <input
                    value={form.contactPersonRole}
                    onChange={(e) => handleChange("contactPersonRole", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Contact email</label>
                  <input
                    value={form.contactEmail}
                    onChange={(e) => handleChange("contactEmail", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Contact phone</label>
                  <input
                    value={form.contactPhone}
                    onChange={(e) => handleChange("contactPhone", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full sm:w-auto rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:bg-gray-400"
                >
                  {saving ? "Saving..." : "Save profile"}
                </button>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Verification documents</p>
                    <p className="mt-1 text-xs text-gray-600">
                      Upload your CAC/registration certificate, tax document, or any proof of business.
                    </p>
                  </div>
                  <label className="inline-flex cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                    {uploading ? "Uploading..." : "Upload document"}
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                      onChange={(e) => handleUploadDoc(e.target.files?.[0], "Company verification")}
                      disabled={uploading}
                    />
                  </label>
                </div>

                {(company.verificationDocuments || []).length ? (
                  <ul className="mt-4 divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
                    {(company.verificationDocuments || [])
                      .slice()
                      .reverse()
                      .map((d) => (
                        <li key={d._id || d.url} className="p-3 text-sm">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">{d.label || "Document"}</p>
                              <p className="text-xs text-gray-500">
                                Status: <span className="font-semibold">{d.status || "PENDING"}</span>
                                {d.rejectionReason ? ` • Reason: ${d.rejectionReason}` : ""}
                              </p>
                            </div>
                            <a
                              href={d.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            >
                              View
                            </a>
                          </div>
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-gray-500">No documents uploaded yet.</p>
                )}
              </div>
            </form>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default EmployerProfile

